import Perfume from 'perfume.js';
import shortid from 'shortid';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { bufferTime, filter } from 'rxjs/operators';

import type { EventSourceContext } from './moMetrics/clickStreamEvent';
import { makeEventName, recordMoMetricsEvent } from './moMetrics';

const metricNames = {
  cls: 'cumulativeLayoutShift',
  dataConsumption: 'dataConsumption',
  fcp: 'firstContentfulPaint',
  fid: 'firstInputDelay',
  fp: 'firstPaint',
  lcp: 'largestContentfulPaint',
  tbt: 'totalBlockingTime'
};

const id = shortid();

type PerfumeEvent = {
  [key: string]: {};
  navigatorInformation: {
    deviceMemory: number;
    hardwareConcurrency: number;
    isLowEndDevice: boolean;
    isLowEndExperience: boolean;
    serviceWorkerStatus: string;
  };
};

const perfumeSubject = new Subject();

const createPerfumeBuffer = (subject: Subject<unknown>, time: number) =>
  subject.pipe(
    bufferTime(time),
    filter(buffer => !!buffer.length)
  );

const subscribeToBuffer = (buffer: Observable<unknown>) =>
  buffer.subscribe({
    next: (metricArray: PerfumeEvent[]) => {
      const metrics = metricArray.reduce(
        (metricObject, nextMetric) => ({
          ...metricObject,
          ...nextMetric
        }),
        {} as PerfumeEvent
      );
      const eventName = makeEventName('Initial Load');
      const eventContext: EventSourceContext = { eventTime: new Date(), eventName };

      recordMoMetricsEvent(eventContext, { ...metrics, shortId: id });
    }
  });

const initPerfume = () => {
  const perfumeBuffer = createPerfumeBuffer(perfumeSubject, 1000);
  subscribeToBuffer(perfumeBuffer);

  const _perfume = new Perfume({
    resourceTiming: true,
    analyticsTracker: options => {
      const { metricName, data, navigatorInformation } = options;
      const metric = metricNames[metricName];
      if (!metric) {
        return undefined;
      }

      return perfumeSubject.next({ [metric]: data, navigatorInformation });
    }
  });
};

export default initPerfume;
export { createPerfumeBuffer, subscribeToBuffer };
